import type { GetServerSidePropsContext } from 'next';
import { useQueries, useQuery } from '@tanstack/react-query';

import { get, post } from 'utils/apiWrapper';
import {
  PRODUCTS_LIST_ROUTE,
  PRODUCT_ROUTE,
  PRODUCT_ROUTE_LIVE,
  CART,
  PRODUCT_CREATE_ORDER,
  PRODUCT_CANCEL_ORDER,
  PRODUCT_GET_ORDER_INFO,
  CHECKOUT_INFO,
  PRODUCT_COLLECTIONS,
  PRODUCTS_CAMPAIGN,
  PRODUCT_SIZE_GUIDE,
  ADD_TO_BAG,
  RETRIEVE_BAG,
  FIND_AVAIL_STORES,
  DELETE_FROM_BAG,
  NOTIFY_ME,
  CHECK_GIFTCARD,
  PRODUCTS_RECOMMENDED,
  ORDER_REJECT_1,
  ORDER_REJECT_2,
  CART_CHANGE_SIZE,
  DELIVERY_POINTS,
  ORDER_SEND_MAIL,
  ORDER_MODIFY_SIZE_1,
  ORDER_MODIFY_SIZE_2,
  CHECK_PRODUCT_IN_WISHLIST,
  SEND_INVOICE,
  CREATE_OFFLINE_ORDER,
  CREATE_CONTINGENCY_ORDER,
  COUNTRIES_IPAD,
  GET_RETURN_LABEL,
  POST_TAKEBACK,
  GET_TAKEBACK_CREDIT,
  GET_OTHER_COLORS,
  GET_DROPPOINT_URL,
  CROSS_SELLING,
  REVIEW_DELIVERY,
  GRID_PROPS,
  GET_ROKU_STOCK,
  PRODUCT_RESERVATION,
  GET_TAKEBACK_ECI_STORES,
} from 'utils/routes';
import {
  targetsMap,
  DEFAULT_LIMIT,
  getRealID,
  handleStorage,
  COOKIES,
  getCookie,
  eraseCookie,
  prepareHeadersFromContext,
  handleOriginByCookie,
  setCookie,
} from 'utils/helpers';

import { AUTO_BONDS } from 'utils/constants/cookies';
import { IS_CAMPER } from 'utils/constants/system';
import { trackCaptchaSuccess } from 'utils/dataLayers';
import { combineTwoShoesPrices } from 'utils/product';
import { getUtmParams } from 'utils/utm';
import { useLocale } from 'hooks';
import { formatProductsForCheckout } from 'utils/checkout';
import { normalizeOrder } from 'utils/order';
import { storage } from 'utils/web-storage';

import type { ApiOK } from 'types/api';
import { queryKeys } from 'utils/constants/reactQuery';
import { sleep, tryCatch } from 'utils/common';
import { $fetch } from 'utils/request';
import type { ServerProductSheet, TRealTarget, TRecommendedProduct } from 'types/product';
import { LOCAL_STORAGE_KEYS } from 'utils/constants/storage';
import type { TProductList } from 'types/product-list';

type TSendInvoice = {
  locator: string;
  profile: string;
  name: string;
  surname: string;
  email: string;
  telephone: string;
};

export const sendInvoice = async (payload: TSendInvoice) => {
  const response = await post({
    path: SEND_INVOICE,
    data: payload,
  });

  if (response) {
    const { data } = response;
    if (data && data.status === 'OK') {
      return data;
    }
  }
  throw new Error('KO');
};

type TGetGridProps = {
  context?: GetServerSidePropsContext;
  locale: string;
  slug: string;
  cache?: string;
  targetId: string;
  familyId: string;
  filters?: Record<string, string>;
  langGenderOrFamily?: boolean;

  // en un futuro añadiremos filters y haremos slug opcional
  // para la tarea de canonical redirect
};

export const getGridProps = ({ context, locale, slug, cache, targetId, familyId, filters, langGenderOrFamily }: TGetGridProps) =>
  new Promise((resolve, reject) => {
    const headers = prepareHeadersFromContext(context as TODO);

    const searchParams = new URLSearchParams({
      profile: locale,
      targetId,
      familyId,
    });

    if (slug) {
      searchParams.append('slug', slug);
    }

    if (cache) {
      searchParams.append('cache', cache);
    }

    if (langGenderOrFamily === true) {
      searchParams.append('langGenderOrFamily', 'true');
    }

    if (filters && Object.keys(filters).length > 0) {
      Object.entries(filters).forEach(([key, value]) => {
        searchParams.append(key, value);
      });
    }

    const path = `${GRID_PROPS}?${searchParams.toString()}`;

    get({
      path,
      headers: headers as TODO,
    })
      .then(({ data }) => {
        if (data.status !== 'OK') {
          reject(`${path} => Error ${data.status} ${data?.errors ? JSON.stringify(data.errors) : ''}`);
        } else {
          resolve(data);
        }
      })
      .catch(reject);
  });

type TGetProducts = {
  context?: GetServerSidePropsContext;
  lang: string;
  target: string | string[];
  page: number;
  filters?: Record<string, string>;
  sort?: string;
  limit?: number;
  product?: string | string[];
  voucherDiscount?: string;
  seoContent?: boolean;
  gridId?: string;
  square?: boolean;
  scope?: 'minimal' | 'lite' | 'full';
};

export const getProducts = ({
  context,
  lang,
  target,
  page,
  filters,
  sort,
  limit = DEFAULT_LIMIT,
  product,
  voucherDiscount,
  seoContent,
  gridId,
  square = false,
  scope,
}: TGetProducts) =>
  new Promise<TProductList>((resolve, reject) => {
    const targetRealID = getRealID('target', target);
    const familyRealID = getRealID('product', product);
    const headers = prepareHeadersFromContext(context as TODO);

    const searchParams = new URLSearchParams({
      profile: lang,
      targetId: targetRealID,
      family: familyRealID,
    });

    if (filters && Object.keys(filters).length > 0) {
      Object.entries(filters).forEach(([key, value]) => {
        searchParams.append(key, value);
      });
    }
    if (sort) {
      searchParams.append('order', sort);
    }
    if (page !== undefined) {
      searchParams.append('pager.offset', page.toString());
      searchParams.append('pager.limit', limit.toString());
    }
    if (voucherDiscount !== undefined && voucherDiscount.length > 0) {
      searchParams.append('voucher', voucherDiscount);
    }
    if (seoContent === true) {
      searchParams.append('seoContent', 'true');
    }
    if (gridId) {
      searchParams.append('gridId', gridId);
    }
    if (square === true) {
      searchParams.append('square', 'true');
    }
    if (scope && scope !== 'full') {
      searchParams.append('scope', scope);
    }

    const path = `${PRODUCTS_LIST_ROUTE}?${searchParams.toString()}`;

    get({ path, headers: headers as TODO })
      .then(({ data }) => {
        const {
          status,
          products,
          availableFilters,
          total,
          currentList,
          currentListId,
          camperlab,
          title,
          description,
          urlCanonical,
          seoContent,
          rewalk,
          relatedCollectionId,
          urlRelatedCollection,
          urlCanonicalGTM,
          vmtoolConfig,
        } = data;

        if (status !== 'OK') {
          reject(`${process.env.API_GATEWAY}${path} => Error ${status} ${data?.errors ? JSON.stringify(data.errors) : ''}`);
        } else {
          if (!context) {
            storage.local.set(LOCAL_STORAGE_KEYS.VMTOOL_CONFIG, vmtoolConfig);
          }

          resolve({
            products,
            total,
            availableFilters,
            currentList,
            currentListId,
            camperlab,
            title,
            description,
            urlCanonical,
            seoContent,
            rewalk,
            relatedCollectionId,
            urlRelatedCollection,
            urlCanonicalGTM,
            vmtoolConfig,
          });
        }
      })
      .catch(reject);
  });

type TGetProduct = { context?: GetServerSidePropsContext; lang: string; target: string; productCode: string; targetIdUrl?: string };

export const getProduct = ({ context, lang, target, productCode }: TGetProduct) =>
  new Promise<ApiOK<{ [key: string]: any }>>((resolve, reject) => {
    let targetRealID = null;

    Object.keys(targetsMap as TODO).forEach((targetKey) => {
      if ((targetsMap as TODO)[targetKey].includes(target)) {
        targetRealID = targetKey;
      }
    });

    const country_price_query = context?.query?.country_price;
    const country_price_cookie = context?.req?.cookies[COOKIES.COUNTRY_PRICE];
    const country_price = country_price_query || country_price_cookie;
    let path = `${PRODUCT_ROUTE}?profile=${lang}&gender=${targetRealID}&material=${productCode}&seoContent=true`;

    if (country_price) {
      path = `${path}&country_price=${country_price}`;
    }

    const headers = prepareHeadersFromContext(context as TODO);
    get({
      path,
      headers: headers as TODO,
    })
      .then(({ data }) => {
        const { status, productSheet, productBetter, seoContent } = data;
        if (status !== 'OK') {
          reject(`${path} => Error ${status} ${data?.errors ? JSON.stringify(data.errors) : ''}`);
        } else {
          resolve({ status, productSheet, productBetter, seoContent });
        }
      })
      .catch(reject);
  });

type GetProductSheetArgs = {
  context?: GetServerSidePropsContext;
  profile: string;
  target: TRealTarget;
  productCode: string;
  seoContent: boolean;
};

export async function getProductSheet({ context, profile, target, productCode, seoContent }: GetProductSheetArgs) {
  let countryPrice = null;

  if (context) {
    countryPrice = context?.query?.country_price ?? context?.req?.cookies[COOKIES.COUNTRY_PRICE] ?? null;
  }
  if (typeof window !== 'undefined') {
    const queryParams = new URLSearchParams(window.location.search);
    const country_price_query = queryParams.get('country_price');
    const country_price_cookie = getCookie(COOKIES.COUNTRY_PRICE);
    countryPrice = country_price_query || country_price_cookie;
  }

  const data = await $fetch<{
    productSheet: ServerProductSheet;
    seoContent: Record<string, any>;
  }>({
    path: PRODUCT_ROUTE,
    query: { profile, gender: target, material: productCode, country_price: (countryPrice ?? '') as TODO, seoContent: String(seoContent) },
  });
  return data;
}

interface TGetProductLive extends TGetProduct {
  voucherDiscount?: string;
}

type TProductLiveResponse = {
  productSheetLive: {
    id: string;
    sizes?: {
      available: boolean;
      label: string;
      value: string;
      quantity: number;
      ean: string;
      price: {
        current: number;
        currency: string;
        currencySap: string;
        pricePerSize: boolean;
        currentFormated: string;
        usrHash: boolean;
        adqCampaign: boolean;
      };
    }[];
    prices?: {
      current: number;
      currentEur: number;
      currency: string;
      currencySap: string;
      discountEur: number;
      discountLocal: number;
      discountPrice: number;
      pricePerSize: boolean;
      currentFormated: string;
      discountPriceFormatted: string;
      usrHash: boolean;
      adqCampaign: boolean;
    };
    kboix: boolean;
  };
};

export const getProductLive = ({ context, lang, target, productCode, voucherDiscount }: TGetProductLive) =>
  new Promise<ApiOK<TProductLiveResponse>>((resolve, reject) => {
    const path = `${PRODUCT_ROUTE_LIVE}`;
    const headers = prepareHeadersFromContext(context as TODO);
    const dataBody: Record<string, string> = {
      profile: lang,
      gender: target,
      material: productCode,
    };
    if (voucherDiscount !== undefined && voucherDiscount.length > 0) {
      dataBody.voucher = voucherDiscount;
    }

    post({
      path,
      headers: headers as TODO,
      data: dataBody,
    })
      .then(({ data }) => {
        const { status, productSheetLive } = data;
        if (status !== 'OK') {
          reject(status);
        } else {
          resolve({ status, productSheetLive });
        }
      })
      .catch(reject);
  });

export const getProductCollection = ({ context, lang, ids }: { context: any; lang: string; ids: string[] }) =>
  new Promise<any[]>((resolve, reject) => {
    const path = `${PRODUCT_COLLECTIONS}?profile=${lang}&filter.id=${ids.join(',')}`;
    const headers = prepareHeadersFromContext(context as TODO);

    get({ path, headers: headers as TODO })
      .then(({ data }) => {
        const { status, products, errors } = data;
        if (status === 'OK') {
          resolve(products);
        } else {
          reject(errors);
        }
      })
      .catch(reject);
  });

type TGetReviews = {
  context?: GetServerSidePropsContext;
  lang: string;
  productCode: string;
  offset: number;
  sortField: string;
  sortType: string;
  filter?: string[];
  langList: string[];
};

export const getReviews = ({ context, lang, productCode, offset, sortField, sortType, filter, langList }: TGetReviews) =>
  new Promise<Record<string, any>>((resolve, reject) => {
    const i18nString = langList; // para no cambiarlo en el path
    const passKey = IS_CAMPER ? 'ca6JviuvigXXPa5VhlSKKDKEAqRo2kb0KkGfb72gGPdfg' : 'caQmurQvx3wq6cMaj35ZRFZQ98Eo62Rb6tsTbJefSGUX8';
    const diplayCode = IS_CAMPER ? '19298-es_es' : '22318-en_gb';

    // lo hago así y le meto un replace después que si no es un cristo
    let path = `?
passkey=${passKey}&
apiversion=5.5&
displaycode=${diplayCode}&
resource.q0=products&
filter.q0=id:eq:${productCode}&
stats.q0=reviews&
filteredstats.q0=reviews&
filter_reviews.q0=isratingsonly:false&
filter_reviews.q0=contentlocale:eq:${i18nString}&
filter_reviewcomments.q0=contentlocale:eq:${i18nString}&
resource.q1=reviews&
filter.q1=productid:eq:${productCode}&
filter.q1=isratingsonly:false&
filter.q1=contentlocale:eq:${i18nString}&
sort.q1=${sortField}:${sortType}&
stats.q1=reviews&
filteredstats.q1=reviews&
include.q1=authors,products,comments&
filter_reviews.q1=contentlocale:eq:${i18nString}&
filter_reviewcomments.q1=contentlocale:eq:${i18nString}&
filter_comments.q1=contentlocale:eq:${i18nString}&
limit.q1=8&
offset.q1=${offset}&
limit_comments.q1=3
`;

    if (filter !== undefined && Array.isArray(filter) && filter.length > 0) {
      path = `${path}&filter.q1=rating:eq:${filter.join(',')}`;
    }

    path = path?.replace(/(\r\n|\n|\r)/gm, '');

    const base = 'https://api.bazaarvoice.com/data/batch.json';

    get({ base, path })
      .then(async ({ data }) => {
        const { BatchedResultsOrder, BatchedResults, HasErrors, Errors } = data;

        if (HasErrors) {
          reject(Errors);
        } else {
          const firstReviews = BatchedResults?.q1;

          if (firstReviews !== undefined && firstReviews.TotalResults !== 0) {
            const { ProductsOrder } = firstReviews.Includes;
            const { Results } = firstReviews;
            const ids = ProductsOrder.filter((item: TODO) => item !== productCode);
            let collectionResponse = [];
            if (ids.length > 0) {
              try {
                collectionResponse = await getProductCollection({
                  context,
                  lang,
                  ids,
                });
              } catch (error) {
                collectionResponse = [];
              }
            }
            for (let i = 0; i < Results.length; i++) {
              const reviewItem: TODO = Results[i];
              const { ProductId } = reviewItem;
              const originalCollection = collectionResponse.filter((item) => item.id === ProductId)[0];

              if (originalCollection !== undefined) {
                reviewItem.Origin = originalCollection.name;
              }
            }
          }

          resolve({ BatchedResultsOrder, BatchedResults });
        }
      })
      .catch(reject);
  });

type TGetCart = {
  context?: GetServerSidePropsContext;
  lang: string;
  products: Record<string, any>[];
  bondValue?: string;
  tax?: Record<string, any>;
  hashOrderId: string | undefined;
  employeeId?: string;
  withPaymentMethods?: boolean | null;
  cookies?: Record<string, any>;
  origin?: string;
  wholesalerData?: Record<string, any>;
};

export const getCart = ({
  context,
  lang,
  products,
  bondValue,
  tax,
  hashOrderId,
  employeeId,
  withPaymentMethods = null,
  cookies = undefined,
  origin = '',
  wholesalerData = {},
}: TGetCart) =>
  new Promise<Record<string, any>>((resolve, reject) => {
    const path = CART;
    let headers: TODO = prepareHeadersFromContext(context as TODO);
    if (process.env.ENVIRONMENT === 'local') {
      const clientId = getCookie(COOKIES.CLIENT);
      if (!headers) {
        headers = {
          [COOKIES.CLIENT]: clientId,
        };
      } else if (!headers[COOKIES.CLIENT]) {
        headers[COOKIES.CLIENT] = clientId;
      }
    }

    const productsFormated = products.map((product) => ({
      id: product.code || product.productId || product.id,
      shoppingCartId: String(product.id),
      size: product.size || '',
      gender: product.target || '',
    }));

    const autoVoucher = (cookies === undefined ? getCookie(COOKIES.AUTO_BONDS) : cookies[COOKIES.AUTO_BONDS]) === AUTO_BONDS.APPLY;
    const payload: Record<string, any> = {
      profile: lang,
      products: productsFormated,
      voucher: bondValue || '',
      employeeId: employeeId || '',
      tax,
      hashOrderId,
      forceSAP: true,
      withPaymentMethods,
      autoVoucher,
      ...wholesalerData,
    };

    if (typeof window !== 'undefined' && window?.document?.cookie) {
      payload.workstation = getCookie(COOKIES.WORKSTATION);
      payload.embeded = getCookie(COOKIES.APP_IPAD);
      payload.tpvStoreId = getCookie(COOKIES.TPV_STORE_ID);
      payload.tpvEmployeeId = getCookie(COOKIES.TPV_EMPLOYEE_ID);
      payload.country_price = getCookie(COOKIES.COUNTRY_PRICE);
    } else if (context && context.req) {
      const {
        [COOKIES.APP_IPAD]: embeded,
        [COOKIES.TPV_STORE_ID]: tpvStoreId,
        [COOKIES.TPV_EMPLOYEE_ID]: tpvEmployeeId,
        [COOKIES.WORKSTATION]: workstation,
        [COOKIES.COUNTRY_PRICE]: country_price,
      } = context?.req?.cookies;
      if (embeded !== undefined) {
        payload.embeded = embeded;
      }
      if (tpvEmployeeId !== undefined) {
        payload.tpvEmployeeId = tpvEmployeeId;
      }
      if (tpvStoreId !== undefined) {
        payload.tpvStoreId = tpvStoreId;
      }
      if (workstation !== undefined) {
        payload.workstation = workstation;
      }
      if (country_price !== undefined) {
        payload.country_price = country_price;
      }
    }

    if (payload.embeded !== undefined && payload.tpvStoreId !== undefined) {
      // appmode forceSap always true
      // no tengo acceso a isBlank desde mi rama ni al cambio de estructura de Dani hasta que lo suyo no se suba a master
      payload.forceSap = true;
    }
    if (origin === 'voucher') {
      payload.applyVoucher = true;
    }

    post({
      path,
      headers: headers as TODO,
      data: payload,
    })
      .then(({ data }) => {
        const { status } = data;
        if (status !== 'OK') {
          reject(data);
        } else {
          resolve({
            ...data,
          });
        }
      })
      .catch(reject);
  });

type TCreateOrder = {
  lang: string;
  products: Record<string, any>[];
  shipmentData: Record<string, any>;
  paymentData: Record<string, any>;
  bondValue: string;
  checkoutExpressHashId?: string;
  shoppingCartHashId?: string;
  expressShoppingHashId?: string;
  storeId?: string;
  paymentInfo?: Record<string, any>;
  paymentMethod?: string;
  paymentMethodReal?: string;
  newsletter: boolean;
  isMobile?: boolean;
  appliedGiftcard?: any;
  paypalPlus?: any;
  employeeId?: string;
  csEmployeeId?: string;
  embeded?: any;
  workstation?: any;
  tpvStoreId?: string;
  tpvEmployeeId?: string;
  shippingCost?: any;
  totalImport: number;
  captchaToken: string | undefined;
  newsletterDiscount?: any;
  wholesalerId?: string;
  isExpress?: boolean;
};

export const createOrder = ({
  lang,
  products,
  shipmentData,
  paymentData,
  bondValue,
  checkoutExpressHashId,
  shoppingCartHashId,
  expressShoppingHashId,
  storeId,
  paymentInfo,
  newsletter,
  isMobile,
  appliedGiftcard,
  paypalPlus,
  employeeId,
  csEmployeeId,
  embeded,
  workstation,
  tpvStoreId,
  tpvEmployeeId,
  shippingCost,
  totalImport,
  captchaToken,
  newsletterDiscount,
  wholesalerId,
  isExpress = false,
}: TCreateOrder) =>
  new Promise((resolve, reject) => {
    // use body
    const path = PRODUCT_CREATE_ORDER;
    const billingAddress: Record<string, any> = {};

    const originFields = handleOriginByCookie();
    const utmParams = getUtmParams();

    if (storeId === undefined && Object.keys(shipmentData).includes('billing-name')) {
      Object.keys(shipmentData).forEach((item) => {
        if (item.startsWith('billing-')) {
          billingAddress[item.replace('billing-', '')] = shipmentData[item];
          delete shipmentData[item];
        }
      });
    }

    const dataToSend: Record<string, any> = {
      profile: lang,
      voucher: bondValue,
      ...shipmentData,
      products: formatProductsForCheckout(products as TODO),
      ...paymentData,
      checkoutExpressHashId,
      shoppingCartHashId,
      expressShoppingHashId,
      paymentInfo,
      newsletter,
      device: isMobile !== undefined && isMobile === true ? 'mobile' : null,
      paypalPlus,
      employeeId,
      csEmployeeId,
      embeded,
      workstation,
      tpvStoreId,
      tpvEmployeeId,
      shippingCost,
      totalImport,
      newsletterDiscount,
      isExpress,
      ...originFields,
      ...utmParams,
    };

    if (Object.keys(billingAddress).length !== 0) {
      dataToSend.billingAddress = billingAddress;
    }

    if (storeId) {
      dataToSend.pickupStoreId = storeId;
      dataToSend.billingAddress = { ...shipmentData };
    }

    if (appliedGiftcard) {
      const { giftCardNum, giftCardPin } = appliedGiftcard;
      dataToSend.giftCardNum = giftCardNum;
      dataToSend.giftCardPin = giftCardPin;
    }
    if (captchaToken) {
      dataToSend.captchaToken = captchaToken;
    }
    if (wholesalerId) {
      dataToSend.wholesalerId = wholesalerId;
    }

    post({
      path,
      data: dataToSend,
    })
      .then(({ data }) => {
        const { status, order, expressShopping, encodedReference } = data;
        const userAlreadySubscribed = data.userAlreadySubscribed !== undefined ? data.userAlreadySubscribed : false;

        // si el pedido tiene token de captcha checkear si ha ido bien o mal y lanzar evento
        if (captchaToken) {
          const errorCode = status === 'KO' && data.errors && data.errors[0] ? data.errors[0].code : null;
          trackCaptchaSuccess(errorCode !== 'E_CAPTCHA_ERROR');
        }

        if (status !== 'OK') {
          reject(data);
        } else {
          handleStorage('removeItem', 'giftCards');
          if (order?.adyenResponse?.resultCode !== 'Pending') {
            eraseCookie(COOKIES.EMPLOYEE_ID);
          }
          resolve({
            status,
            order,
            userAlreadySubscribed,
            expressShopping,
            encodedReference,
          });
        }
      })
      .catch((reason) => {
        reject(reason);
      });
  });

type TCancelOrder = {
  lang: string;
  mail: string;
  order: string;
  reason: number;
};

export const cancelOrder = ({ lang, mail, order, reason }: TCancelOrder): Promise<Record<string, any>> =>
  new Promise((resolve, reject) => {
    const path = PRODUCT_CANCEL_ORDER;
    const dataToSend = {
      profile: lang,
      mail,
      order,
      reason,
    };
    post({
      path,
      data: dataToSend,
      headers: {
        'content-type': 'application/json',
      } as TODO,
    })
      .then(({ data }) => {
        delete data.audit;
        resolve(data);
      })
      .catch(reject);
  });

type CancelOrderArgs = {
  profile: string;
  mail: string;
  order: string;
  reason: number;
};

export async function cancelOrderV2(body: CancelOrderArgs) {
  return await $fetch(
    { path: PRODUCT_CANCEL_ORDER },
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}

type TGetOrderInfo = {
  context: any;
  lang: string;
  email: string;
  locator: string;
  offlineReference?: string;
  store?: string;
  fiscalYear?: string;
  includeReturnLabel?: boolean;
};

export const getOrderInfo = ({ context, lang, email, locator, offlineReference, store, fiscalYear, includeReturnLabel }: TGetOrderInfo) =>
  new Promise<Record<string, any>>((resolve, reject) => {
    const path = offlineReference ? `${PRODUCT_GET_ORDER_INFO}?profile=${lang}` : `${PRODUCT_GET_ORDER_INFO}?profile=${lang}&email=${email}&locator=${locator}`;
    const payload =
      !offlineReference ?
        {
          profile: lang,
          email,
          locator,
          includeReturnLabel,
        }
      : {
          profile: lang,
          offlineReference,
          store,
          fiscalYear,
          includeReturnLabel,
        };

    let headers: Record<string, any> = prepareHeadersFromContext(context as TODO) as TODO;
    if (headers === null) {
      headers = {};
    }
    headers['content-type'] = 'application/json';

    post({
      path,
      headers: headers as TODO,
      data: payload,
    })
      .then(({ data }) => {
        const { status } = data;

        if (status !== 'OK') {
          reject(data);
        } else {
          resolve(normalizeOrder(data));
        }
      })
      .catch(reject);
  });

export const reviewDelivery = async (payload: TODO) => {
  const response = await post({
    path: REVIEW_DELIVERY,
    data: payload,
  });

  if (response) {
    const { data } = response;
    if (data && data.status === 'OK') {
      return data;
    }
  }
  throw new Error('KO');
};

export const getCheckoutInfo = ({
  context,
  lang,
  products,
  voucher,
  tax,
  withPaymentMethods,
  embeded,
  workstation,
  employeeId,
  csEmployeeId,
  tpvStoreId,
  tpvEmployeeId,
  wholesalerMail,
  hashOrderId,
}: TODO) =>
  new Promise((resolve, reject) => {
    const path = `${CHECKOUT_INFO}`;
    const headers = prepareHeadersFromContext(context as TODO);
    const { cookies } = (context?.req ?? {}) as TODO;
    const countryPrice = cookies?.[COOKIES.COUNTRY_PRICE] ?? '';

    const payload = {
      profile: lang,
      products,
      voucher,
      tax,
      withPaymentMethods,
      embeded,
      workstation,
      employeeId,
      csEmployeeId,
      tpvStoreId,
      tpvEmployeeId,
      country_price: countryPrice,
      wholesalerMail,
      hashOrderId,
    };

    console.log(path, JSON.stringify(payload, null, 2));

    post({
      path,
      headers: headers as TODO,
      data: payload,
    }).then((response) => {
      const { data } = response;
      if (data.status === 'OK') {
        delete data.audit;
        delete data.status;
        resolve(data);
      } else {
        reject(data.errors);
      }
    });
  });

export const getCampaign = ({ context, lang, camp, family, pager, target, filter, timestamp, voucherDiscount }: TODO) =>
  new Promise((resolve, reject) => {
    let path = `${PRODUCTS_CAMPAIGN}?profile=${lang}&camp=${camp}&family=${family}&pager=${pager}&target=${target}`;
    if (filter) {
      path = `${path}&filter.collection=${filter}`;
    }
    if (timestamp) {
      path = `${path}&timestamp=${Date.now()}`;
    }
    if (voucherDiscount !== undefined && voucherDiscount.length > 0) {
      path = `${path}&voucher=${voucherDiscount}`;
    }
    const headers = prepareHeadersFromContext(context as TODO);

    get({ path, headers: headers as TODO })
      .then(({ data }) => {
        if (data.campaign === undefined) {
          reject(data);
        } else {
          resolve(data.campaign);
        }
      })
      .catch(reject);
  });

export const getSizeGuide = ({ context, lang, material }: TODO) =>
  new Promise((resolve, reject) => {
    const path = `${PRODUCT_SIZE_GUIDE}?profile=${lang}&material=${material}`;
    const headers = prepareHeadersFromContext(context as TODO);

    get({ path, headers: headers as TODO })
      .then(({ data, status }) => {
        if (data.sizeGuide !== undefined) {
          resolve(data.sizeGuide);
          return;
        }
        if (data.sizeGuideClothes !== undefined) {
          resolve(data.sizeGuideClothes);
          return;
        }
        if (data.status === 'OK') {
          resolve(null);
          return;
        }
        reject(data);
      })
      .catch(reject);
  });

export const addToBag = (payload: TODO) =>
  new Promise<ApiOK<{ [key: string]: any }>>((resolve, reject) => {
    const path = ADD_TO_BAG;

    if (window.location) {
      // como solo se llama a addToBag desde el cliente siempre debería haber window.location
      const queryParams = new URLSearchParams(window.location.search);
      const country_price_query = queryParams.get('country_price');
      const country_price_cookie = getCookie(COOKIES.COUNTRY_PRICE);
      const country_price = country_price_query || country_price_cookie;

      if (country_price) {
        setCookie(COOKIES.COUNTRY_PRICE, country_price, 1);
        payload.country_price = country_price;
      }
    }

    const clientId = getCookie(COOKIES.CLIENT);
    let headers: TODO = undefined;
    if (clientId) {
      headers = {
        [COOKIES.CLIENT]: clientId,
      } as TODO;
    }
    // para el carrito abandonado se envia el usrhash si existe.
    const usrhashCookie = getCookie(COOKIES.SELLIGENT_USRHASH);
    if (usrhashCookie) {
      payload.usrHash = usrhashCookie;
    }

    post({
      path,
      headers,
      data: payload,
    })
      .then(({ data }) => {
        resolve(data);
      })
      .catch(reject);
  });

export const retrieveBag = ({ context, payload }: TODO) =>
  new Promise<ApiOK<{ [key: string]: any }>>((resolve, reject) => {
    const path = RETRIEVE_BAG;
    const headers = prepareHeadersFromContext(context as TODO);

    post({
      path,
      headers: headers as TODO,
      data: payload,
    })
      .then(({ data }) => {
        if (!data.shoppingCart || data.shoppingCart.length === 0) {
          console.error('Error retrieving bag', path, headers, payload, data);
        }
        resolve(data);
      })
      .catch((error) => {
        console.error('Error retrieving bag', path, headers, payload, error);
        reject(error);
      });
  });

export const cartChangeSize = (payload: Record<string, any>) =>
  new Promise<ApiOK<{ [key: string]: any }>>((resolve, reject) => {
    const path = CART_CHANGE_SIZE;
    post({
      path,
      data: payload,
    })
      .then(({ data }) => {
        resolve(data);
      })
      .catch(reject);
  });

export const deleteFromBag = (payload: Record<string, any>) =>
  new Promise<ApiOK<{ [key: string]: any }>>((resolve, reject) => {
    const path = DELETE_FROM_BAG;
    post({
      path,
      data: payload,
    })
      .then(({ data }) => {
        resolve(data);
      })
      .catch(reject);
  });

export const findStores = ({ lang, productId, longitude, latitude, size, takeback }: TODO) =>
  new Promise((resolve, reject) => {
    const path = FIND_AVAIL_STORES;
    const payload = {
      profile: lang,
      productId,
      longitude,
      latitude,
      size,
      takeback: takeback || false,
    };
    if (takeback) {
      delete payload.productId;
    }
    post({
      path,
      data: payload,
    }).then(({ data }) => {
      if (data.status === 'OK' && data.stores) {
        resolve(data.stores);
      } else {
        reject(data.status);
      }
    });
  });

export const notifyMe = ({ profile, productId, name, email, size, newsletter, notifymeForSize, lab = false, phonePrefix = '', telephone = '' }: TODO) =>
  new Promise((resolve, reject) => {
    const path = NOTIFY_ME;
    post({
      path,
      data: {
        profile,
        productId,
        size,
        name,
        email,
        newsletter,
        notifymeForSize,
        lab,
        phonePrefix,
        telephone,
      },
    }).then(({ data }) => {
      if (data.status === 'OK') {
        resolve(data);
      } else {
        reject(data.status);
      }
    });
  });

export const checkGiftCard = (payload: Record<string, any>) =>
  new Promise((resolve, reject) => {
    const path = CHECK_GIFTCARD;
    post({
      path,
      data: payload,
    })
      .then(({ data }) => {
        resolve(data);
      })
      .catch(reject);
  });

export const getProductsRecommended = ({ lang, productCode, ia, target, forModal = false, targetIdUrl, voucher, algorithm, square }: TODO) =>
  new Promise<Record<string, any>>((resolve, reject) => {
    let path = `${PRODUCTS_RECOMMENDED}?profile=${lang}&targetIdUrl=${targetIdUrl}&material=${productCode}&useIA=${ia === 'true'}${target ? `&gender=${target}` : ''}${voucher ? `&voucher=${voucher}` : ''}${algorithm ? `&algorithm=${algorithm}` : ''} ${`&square=${square}`}`;

    if (forModal) {
      path = `${path}&forModal=true`;
    }

    get({ path })
      .then(({ data }) => {
        const { status, recommendeds, listName } = data;
        if (status !== 'OK') {
          reject(status);
        } else {
          // inject from param for analytics
          recommendeds.map((re: TODO) => (re.url = `${re.url}`));
          resolve({ status, recommendeds, listName });
        }
      })
      .catch(reject);
  });

export const rejectOrder1 = ({
  lang,
  email,
  reference,
  reason,
  subReason,
  originalStore,
  originalExpedition,
  expeditionCenter,
  rejectProducts,
  region,
}: TODO) =>
  new Promise((resolve, reject) => {
    const path = ORDER_REJECT_1(reference);

    post({
      path,
      data: {
        profile: lang,
        reference,
        email,
        reason,
        subReason,
        originalStore,
        originalExpedition,
        expeditionCenter,
        rejectProducts,
        region,
      },
      headers: {
        'content-type': 'application/json',
      } as TODO,
    })
      .then(({ data }) => {
        const { status, products, shipmentsCosts, pickup, stores, cities } = data;
        if (status !== 'OK') {
          reject(status);
        } else {
          resolve({
            status,
            products,
            shipmentsCosts,
            pickup,
            stores,
            cities,
          });
        }
      })
      .catch(reject);
  });

export const rejectOrder2 = ({
  lang,
  order,
  reason,
  subReason,
  rejectProducts,
  shippingMethodId,
  useNewAddress,
  returnDate,
  applicant,
  expeditionCenterType,
}: TODO) =>
  new Promise((resolve, reject) => {
    const { reference, store, expedition, expeditionCenter, countryId, version } = order;
    const path = ORDER_REJECT_2(reference);
    const rqData = {
      profile: lang,
      reference,
      ...applicant,
      country: countryId,
      originalStore: store,
      originalExpedition: expedition,
      expeditionCenter: expeditionCenter,
      reason,
      subReason,
      rejectProducts,
      shippingMethodId,
      useNewAddress,
      returnDate,
      version,
      expeditionCenterType,
    };
    if (order.receiver?.regionCode) {
      rqData.region = order.receiver.regionCode;
    }
    post({
      path,
      data: rqData,
      headers: {
        'content-type': 'application/json',
      } as TODO,
    })
      .then(({ data }) => {
        const { status, errors } = data;
        if (status === 'KO') {
          let errorMessage = 'KO';
          if (errors && errors.length) {
            errorMessage = `${errors[0].code}: ${errors[0].message}`;
          }
          reject(errorMessage);
        } else {
          resolve({
            status,
            ...data,
          });
        }
      })
      .catch(reject);
  });

export const getDeliveryPoints = ({ context, profile, deliveryType, countryId, city, zipCode }: TODO) =>
  new Promise((resolve, reject) => {
    const path = DELIVERY_POINTS;
    let headers: TODO = prepareHeadersFromContext(context as TODO);
    if (headers === null) {
      headers = {};
    }
    headers['content-type'] = 'application/json';

    post({
      path,
      headers: headers as TODO,
      data: {
        profile,
        deliveryType,
        countryId,
        city: city || '',
        zipCode,
      },
    })
      .then(({ data }) => {
        const { status } = data;

        if (status !== 'OK') {
          const { errors } = data;
          if (errors) {
            reject(errors);
          } else {
            reject(status);
          }
        } else {
          resolve({
            ...data,
          });
        }
      })
      .catch(reject);
  });

export const orderSendMail = ({ profile, email, locator }: TODO) =>
  new Promise((resolve, reject) => {
    const path = ORDER_SEND_MAIL;

    post({
      path,
      data: {
        profile,
        email,
        locator,
      },
      headers: {
        'content-type': 'application/json',
      } as TODO,
    })
      .then(({ data }) => {
        const { status } = data;

        if (status !== 'OK') {
          reject(status);
        } else {
          resolve({
            ...data,
          });
        }
      })
      .catch(reject);
  });

export const modifySize1 = ({
  profile,
  email,
  reference,
  reason,
  subReason,
  originalStore,
  originalExpedition,
  expeditionCenter,
  rejectProducts,
  region,
}: TODO) =>
  new Promise((resolve, reject) => {
    const path = ORDER_MODIFY_SIZE_1(reference);

    post({
      path,
      data: {
        profile,
        reference,
        email,
        reason,
        subReason,
        originalStore,
        originalExpedition,
        expeditionCenter,
        rejectProducts,
        region,
      },
      headers: {
        'content-type': 'application/json',
      } as TODO,
    })
      .then(({ data }) => {
        const { status, products, shipmentsCosts, pickup, defaultPickup, paymentMethods, productShipmentCost, banks } = data;
        if (status !== 'OK') {
          reject(status);
        } else {
          resolve({
            status,
            products,
            shipmentsCosts,
            pickup,
            defaultPickup,
            paymentMethods,
            productShipmentCost,
            banks,
          });
        }
      })
      .catch(reject);
  });

export const modifySize2 = (payload: Record<string, any>) =>
  new Promise((resolve, reject) => {
    const { reference } = payload;
    const path = ORDER_MODIFY_SIZE_2(reference);

    post({
      path,
      data: payload,
      headers: {
        'content-type': 'application/json',
      } as TODO,
    })
      .then(({ data }) => {
        const { status, message, orderId, returnId, adyenResponse, pdfUrl, redirectUrl, resp3ds2, respEissueUrl, errors } = data;
        if (status === 'KO') {
          let errorMessage = 'KO';
          if (errors && errors.length) {
            errorMessage = `${errors[0].code}: ${errors[0].message}`;
          }
          reject(errorMessage);
        } else {
          resolve({
            status,
            message,
            orderId,
            returnId,
            adyenResponse,
            pdfUrl,
            redirectUrl,
            resp3ds2,
            respEissueUrl,
            errors,
          });
        }
      })
      .catch(reject);
  });

export const checkProductInWishList = ({ profile, productId }: { profile: string; productId: string }) =>
  new Promise((resolve, reject) => {
    const token = localStorage.getItem('token');
    post({
      path: CHECK_PRODUCT_IN_WISHLIST,
      data: {
        profile,
        productId,
      },
      headers: { Authorization: `Bearer ${token}` } as TODO,
    })
      .then(({ data }) => {
        const { status } = data;

        if (status !== 'OK') {
          reject(status);
        } else {
          resolve(data.wishlist);
        }
      })
      .catch(reject);
  });

export const createOfflineOrder = ({ profile, payload }: { profile: string; payload: Record<string, any> }) =>
  new Promise((resolve, reject) => {
    post({
      path: CREATE_OFFLINE_ORDER,
      data: {
        profile,
        ...payload,
      },
    })
      .then(({ data }) => {
        const { status } = data;

        if (status !== 'OK') {
          reject(status);
        } else {
          resolve(data);
        }
      })
      .catch(reject);
  });

export const createContingencyOrder = ({
  lang,
  products,
  shipmentData,
  paymentData,
  bondValue,
  checkoutExpressHashId,
  shoppingCartHashId,
  expressShoppingHashId,
  storeId,
  paymentInfo,
  newsletter,
  isMobile,
  appliedGiftcard,
  paypalPlus,
  employeeId,
  csEmployeeId,
  embeded,
  workstation,
  tpvStoreId,
  tpvEmployeeId,
  newsletterDiscount,
  wholesalerId,
}: TODO) =>
  new Promise((resolve, reject) => {
    // use body
    const path = CREATE_CONTINGENCY_ORDER;
    const billingAddress: Record<string, any> = {};

    const originFields = handleOriginByCookie();
    const utmParams = getUtmParams();

    if (storeId === undefined && Object.keys(shipmentData).includes('billing-name')) {
      Object.keys(shipmentData).forEach((item) => {
        if (item.startsWith('billing-')) {
          billingAddress[item.replace('billing-', '')] = shipmentData[item];
          delete shipmentData[item];
        }
      });
    }

    const dataToSend = {
      profile: lang,
      voucher: bondValue,
      ...shipmentData,
      products: formatProductsForCheckout(products as TODO),
      ...paymentData,
      checkoutExpressHashId,
      shoppingCartHashId,
      expressShoppingHashId,
      paymentInfo,
      newsletter,
      device: isMobile !== undefined && isMobile === true ? 'mobile' : null,
      paypalPlus,
      employeeId,
      csEmployeeId,
      embeded,
      workstation,
      tpvStoreId,
      tpvEmployeeId,
      newsletterDiscount,
      ...originFields,
      ...utmParams,
    };

    if (Object.keys(billingAddress).length !== 0) {
      dataToSend.billingAddress = billingAddress;
    }

    if (storeId) {
      dataToSend.pickupStoreId = storeId;
      dataToSend.billingAddress = { ...shipmentData };
    }

    if (appliedGiftcard) {
      const { giftCardNum, giftCardPin } = appliedGiftcard;
      dataToSend.giftCardNum = giftCardNum;
      dataToSend.giftCardPin = giftCardPin;
    }

    if (wholesalerId) {
      dataToSend.wholesalerId = wholesalerId;
    }

    post({
      path,
      data: dataToSend,
    })
      .then(({ data }) => {
        const { status, order, expressShopping, encodedReference } = data;
        const userAlreadySubscribed = data.userAlreadySubscribed !== undefined ? data.userAlreadySubscribed : false;
        if (status !== 'OK') {
          reject(data);
        } else {
          handleStorage('removeItem', 'giftCards');
          eraseCookie(COOKIES.EMPLOYEE_ID);
          resolve({
            status,
            order,
            userAlreadySubscribed,
            expressShopping,
            encodedReference,
          });
        }
      })
      .catch(reject);
  });

export const getIpadCountries = ({ context, profile }: TODO) =>
  new Promise((resolve, reject) => {
    const headers = prepareHeadersFromContext(context as TODO);

    get({ path: `${COUNTRIES_IPAD}?profile=${profile}`, headers: headers as TODO })
      .then((response) => {
        if (response) {
          const { data } = response;
          if (data && data.status === 'OK') {
            resolve(data.countries);
          }
        }
      })
      .catch(reject);
  });

export const getReturnLabel = ({ profile, email, locator }: TODO) =>
  new Promise<ApiOK<{ [key: string]: any }>>((resolve, reject) => {
    post({
      path: GET_RETURN_LABEL,
      data: {
        profile,
        email,
        locator,
      },
    })
      .then((response) => {
        if (response?.data?.status === 'OK') {
          resolve(response.data);
        }

        // PRUEBA POPUP ERROR
        reject(response.data);

        // reject(response);
      })
      .catch(reject);
  });

export const postTakeback = (formData: TODO) =>
  new Promise((resolve, reject) => {
    post({
      path: POST_TAKEBACK,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      } as TODO,
    })
      .then(({ data }) => {
        const { status } = data;

        if (status !== 'OK') {
          reject(status);
        } else {
          resolve(data);
        }
      })
      .catch(reject);
  });

export const getTakebackCredit = (profile: TODO) =>
  new Promise((resolve, reject) => {
    get({ path: `${GET_TAKEBACK_CREDIT}?profile=${profile}` })
      .then((response) => {
        if (response) {
          const { data } = response;
          if (data && data.status === 'OK') {
            resolve(data);
          }
        }
      })
      .catch(reject);
  });

export const getOtherColors = ({ context, lang, target, id, voucher }: TODO) =>
  new Promise<Record<string, any>>((resolve, reject) => {
    const path = `${GET_OTHER_COLORS}?profile=${lang}&material=${id}&gender=${target}${voucher ? `&voucher=${voucher}` : ''}`;
    const headers = prepareHeadersFromContext(context as TODO);

    get({ path, headers: headers as TODO })
      .then(({ data }) => {
        const { status, products, listName } = data;

        if (status === 'OK' && products?.length > 0) {
          const productsToReturn = products.map((re: TODO) => {
            re.url = `${re.url}?from=${id}`;
            return re;
          });
          resolve({ products: productsToReturn, listName });
        } else {
          reject(data);
        }
      })
      .catch(reject);
  });

/**
 * @typedef {object} Droppoint
 * @property {string} title
 * @property {string} url
 * @property {string} description
 * @property {string} buttonText
 * @property {string} imgUrl
 */
/**
 * @typedef {object} DroppointResponse
 * @property {string} droppointUrl
 * @property {Droppoint[]} droppoints
 */
/**
 *
 * @param {object} context
 * @param {string} profile
 * @param {string|null} type
 * @returns {DroppointResponse}
 */
export async function getDroppointsInfo(context: TODO, profile: TODO, type: TODO = null) {
  const headers = prepareHeadersFromContext(context as TODO);
  const path = `${GET_DROPPOINT_URL}?profile=${profile}${type ? `&type=${type}` : ''}`;

  const { data } = await get({ path, headers: headers as TODO });

  const { status, droppointUrl, droppoints } = data;
  if (status === 'OK') {
    return { droppointUrl, droppoints };
  }
  throw data;
}

type RCrossSelling = {
  recommendeds: TRecommendedProduct[];
};

export async function getCrossSelling({ profile, productId }: { profile: string; productId: string }) {
  const { data, error } = await tryCatch($fetch<RCrossSelling>({ path: CROSS_SELLING, query: { profile, productId } }));
  if (error) {
    return [];
  }
  return data.recommendeds ?? [];
}

export const getRokuStock = async ({ context, profile, size }: TODO) => {
  const headers = prepareHeadersFromContext(context as TODO);

  return post({
    path: GET_ROKU_STOCK,
    headers: headers as TODO,
    data: {
      profile,
      size,
    },
  })
    .then(({ data }) => {
      const { status, noStockProducts } = data;
      if (status === 'OK') {
        return noStockProducts;
      }

      return false;
    })
    .catch((error) => {
      console.error(error);
      return false;
    });
};

export async function getSunneiProductData({ sunneiInfo, locale, context, target }: TODO) {
  // necesitamos 2 cosas, traer toda la info de los skus de sunnei y toda la info de los productSheetLive y hacer combinaciones de precios y tallas
  const sunneiSkus: string[] = Array.from(new Set(sunneiInfo?.all ?? []));
  if (!sunneiSkus.length) {
    return {
      error: true,
      message: 'No products found',
    };
  }

  // necesitamos crear promesas con cada sku
  const sheetPromises: Promise<any>[] = [];
  const livePromises: Promise<any>[] = [];
  sunneiSkus.forEach((sku: string) => {
    sheetPromises.push(
      getProduct({
        context,
        lang: locale,
        target,
        productCode: sku,
        targetIdUrl: target,
      }),
    );
    livePromises.push(
      getProductLive({
        context,
        lang: locale,
        target,
        productCode: sku,
      }),
    );
  });

  // llamamos a todas las promesas a la vez

  const [sheetResponses, liveResponses] = await Promise.all([Promise.all(sheetPromises), Promise.all(livePromises)]);
  // en un producto normal lo que se cambia son los sizes y los voucher prices.
  const newSunneiData = sheetResponses.reduce((acc: TODO, sheet: TODO, index: number) => {
    let live = liveResponses[index];
    // check ids
    if (!live || live.productSheetLive.id !== sheet.productSheet.id) {
      const foundLiveWithSameId = (liveResponses as TODO).find((l: TODO) => l.productSheet.id === sheet.productSheet.id);
      if (foundLiveWithSameId) {
        live = foundLiveWithSameId;
      } else {
        return acc;
      }
    }
    if (sheet.status === 'OK' && live.status === 'OK') {
      acc[sheet.productSheet.id] = {
        ...sheet.productSheet,
        sizes: live.productSheetLive?.sizes ?? [],
        images: sheet.productSheet.views ?? null,
        imagesMobile: sheet.productSheet?.viewsMobile ?? null,
        voucherPrices: live?.productSheetLive?.voucherPrices ?? null,
      };
    }
    return acc;
  }, {});
  const aggregateSizes = liveResponses.reduce((acc: Map<string, TODO>, live: TODO) => {
    if (live.status === 'OK' && Array.isArray(live?.productSheetLive?.sizes)) {
      (live.productSheetLive.sizes as TODO[]).forEach((size: TODO) => {
        // primero si existe el size y el actual tiene menos stock que el anterior, no lo metemos
        if (acc.has(size.value) && acc.get(size.value).quantity > size.quantity) {
          return;
        }
        acc.set(size.value, size);
      });
    }
    return acc;
  }, new Map());

  const firstShoeSheet = newSunneiData[sunneiInfo.shoeOne];
  const secondShoeSheet = newSunneiData[sunneiInfo.shoeTwo];

  const combinedPrices = combineTwoShoesPrices({
    shoeOnePrice: firstShoeSheet.prices,
    shoeTwoPrice: secondShoeSheet.prices,
  });
  const json = JSON.parse(
    JSON.stringify({
      data: newSunneiData,
      sizes: Array.from(aggregateSizes.values()),
      price: combinedPrices,
      skus: sunneiSkus,
    }),
  );
  return json;
}

export function useLookStock({ skus, target }: { skus: string[]; target: string }) {
  const { locale } = useLocale();
  return useQueries({
    queries: skus.map((sku) => ({
      queryKey: [queryKeys.product.live, sku],
      queryFn: () =>
        getProductLive({
          lang: locale,
          target,
          productCode: sku,
        }),
    })),
    combine: (result) => {
      const dataResult = result.reduce((acc: TODO, r: TODO) => {
        if (r.status === 'success' && r.data.productSheetLive.prices && r.data.productSheetLive.sizes) {
          acc[r.data.productSheetLive.id] = {
            sizes: r.data.productSheetLive.sizes,
            prices: r.data.productSheetLive.prices,
          };
        }
        return acc;
      }, {});
      return {
        data: dataResult,
        empty: Object.keys(dataResult).length === 0,
        isPending: result.some((r) => r.isPending),
      };
    },
  });
}

export async function getKboixLiveData({ midsoles, context, locale, casco, target }: TODO) {
  const midsolePromises = midsoles.map((midsole: TODO) =>
    getProductLive({
      context,
      lang: locale,
      target: midsole.gender,
      productCode: midsole.id,
    }),
  );
  const cascoPromise = getProductLive({
    context,
    lang: locale,
    target,
    productCode: casco,
  });
  try {
    const [cascoResponse, ...midsoleResponses]: [Record<string, any>, ...Record<string, any>[]] = await Promise.all([cascoPromise, ...midsolePromises]);
    const midsolesData = midsoles.map((midsole: TODO) => {
      const foundResponse = (midsoleResponses as TODO[]).find((m: TODO) => m.productSheetLive.id === midsole.id);
      if (foundResponse) {
        const sizes = foundResponse.productSheetLive.sizes ?? [];
        return { ...midsole, sizes, prices: foundResponse.productSheetLive.prices ?? [] };
      }
      return midsole;
    });
    return { casco: { sizes: cascoResponse.productSheetLive.sizes, prices: cascoResponse.productSheetLive.prices }, midsoles: midsolesData };
  } catch (e) {
    console.error(e);
    return null;
  }
}

export async function reserveProduct({ profile, store_id, email, sku, phone, name }: TODO) {
  const path = PRODUCT_RESERVATION;

  const response = await post({
    path,
    data: {
      profile,
      store_id,
      email,
      sku,
      phone,
      name,
    },
  });

  try {
    const { data } = response;
    const { status } = data;

    if (status === 'OK') {
      return true;
    }
  } catch (e) {
    console.error(e);
  }

  return false;
}

export async function getTakebackECIStores(profile: TODO) {
  try {
    const path = `${GET_TAKEBACK_ECI_STORES}?profile=${profile}`;

    const response = await get({ path });
    const { data } = response;
    const { status, stores } = data;

    if (status === 'OK') {
      return stores;
    }
  } catch (e) {
    console.error(e);
  }

  return [];
}

export async function getGridProductsById({ profile, skus, target, square }: { profile: string; skus: string[]; target: string; square: boolean }) {
  const data = await $fetch<TProductList>({
    path: PRODUCTS_LIST_ROUTE,
    query: { profile, ['filter.id']: skus.join(','), targetId: target, square, scope: 'lite' },
  });
  if (Array.isArray(data.products) && data.products.length > 0) {
    return data.products;
  }
  throw new Error('No products found');
}

export function useProductSheets({ products, delay }: { products: { id: string; target: TRealTarget }[]; delay?: number }) {
  const { locale } = useLocale();
  return useQueries({
    queries: products.map(({ id, target }) => ({
      queryKey: [queryKeys.product.sheet, id, target],
      queryFn: async () => {
        const [result] = await Promise.all([
          getProductSheet({
            profile: locale,
            productCode: id,
            target: target,
            seoContent: false,
          }),
          sleep(delay ?? 0),
        ]);
        return result;
      },
    })),
    combine: (result) => {
      const dataResult = result.reduce(
        (acc, r) => {
          if (r.status === 'success' && r.data.productSheet && r.data.productSheet.exists === true) {
            acc[r.data.productSheet.id] = r.data.productSheet;
          }
          return acc;
        },
        {} as Record<string, ServerProductSheet>,
      );

      return {
        data: dataResult,
        empty: Object.keys(dataResult).length === 0,
        isPending: result.some((r) => r.isPending),
      };
    },
  });
}

export function useProductLive({ sku, target }: { sku: string; target: string }) {
  const { locale } = useLocale();
  return useQuery({
    queryKey: [queryKeys.product.live, sku],
    queryFn: () =>
      getProductLive({
        lang: locale,
        target,
        productCode: sku,
      }),
  });
}
